import {QuotationSchema} from "../types";
import React from "react";
import {MTColumnsType} from "@utils/pagination";
import {FormattedMessage} from "react-intl";
import {Link} from "react-router-dom";
import {ArrowTopRightOnSquareIcon, TrashIcon} from "@heroicons/react/24/outline";
import {formatCurrency} from "../quotation-draft/lib/format";
import {UserTableFilter} from "@components/UserTableFilter";
import {DateRangeFilter} from "../../../utils/pagination/types";
import MTDateRangePicker from "@ui-components/MTDateRangePicker";
import WorksiteNameColumn from "../../../components/WorksiteNameColumn";

export const fixEndDate = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}:23:59:59`
export const fixStartDate = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}:00:00:00`

export const getColumns = (
  countryCode: string,
  userId: number,
  userType: string,
  onDelete: (id: number) => void,
  userScopes: string[],
  intl: any
) : MTColumnsType<QuotationSchema> => [
    {
      title: "",  // actions
      filtering: false,
      sorting: false,
      render: ({id, w_perm}) => {
        const canDelete = w_perm.some((p) => ['public', userType, `user_${userId}`].includes(p)) && userScopes.includes('quotation:delete');
        return (
          <div className="flex flex-row gap-5">
            <Link to={`/${countryCode}/quotations/${id}`}>
              <ArrowTopRightOnSquareIcon className="w-6 text-kerakoll-700"/>
            </Link>
            {canDelete && (
              <TrashIcon
                className="w-6 text-red-500 cursor-pointer"
                onClick={() => onDelete(id)}
              />
            )}
          </div>
        )
      }
    },
    {
      title: <FormattedMessage id="worksite"/>,
      type: "string",
      field: "worksites.address",
      render: ({worksites: {id, city, address, des_worksite}}) => <>
        <WorksiteNameColumn
          address={address}
          des_worksite={des_worksite}
          city={city}
          id={id}
          countryCode={countryCode}
          />
      </>,
      headerStyle: {minWidth: 250},
      pagination: {
        spech: {
          column_name: [
            "worksites.des_worksite",
            "worksites.address",
            "worksites.city",
          ]
        }
      }
    },
    {
      title: <FormattedMessage id="configurations"/>,
      field: "surveys",
      render: ({worksites: {id: worksiteId}, surveys}) => (
        <ul className="list-disc list-inside">
          {surveys?.map(({id, name, service_id}, idx) => (
            <li key={idx}>
            <span className={!name ? 'text-gray-400' : ''}>
              {name || <FormattedMessage id="unnamed_configuration"/>}
            </span>
              <Link
                to={`/${countryCode}/worksites/${worksiteId}/service/${service_id}/survey/${id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArrowTopRightOnSquareIcon className="inline w-4 ml-2 -mt-0.5 text-kerakoll-700"/>
              </Link>
            </li>
          ))}
        </ul>
      ),
      headerStyle: {minWidth: 180},
      filtering: false,
    },
    {
      title: <FormattedMessage id="name"/>,
      field: "des_title",
      type: "string",
      hidden: true,
      headerStyle: {minWidth: 250},
    },
    {
      title: <FormattedMessage id="created_by"/>,
      field: "des_creator",
      type: "string",
      filterComponent: (props) => <UserTableFilter {...props} onlyInteral={true} />,
      render: ({des_creator}) => des_creator,
      headerStyle: {minWidth: 180},
      pagination: {
        spech: {
          column_name: "id_creator",
          filter_operator: "=",
          value_parser: (value) => parseInt(value),
        }
      },
    },
    {
      title: <FormattedMessage id="created_at"/>,
      field: "created_at",
      type: "date",
      defaultSort: "desc",
      minWidth: 300,
      pagination: {
        spech: {
          column_name: "created_at",
          value_parser: (value: DateRangeFilter) => {
            const {startDate, endDate} = value;
            if (startDate && endDate) {
              return [fixStartDate(startDate), fixEndDate(endDate)];
            }
            if (startDate && !endDate) {
              return fixStartDate(startDate)
            }
            if (endDate && !startDate) {
              return fixEndDate(endDate)
            }
            return null
          },
          date_range_filter: true,
        }
      },
      align: "center",
      render: ({created_at}) => Intl.DateTimeFormat("it-IT").format(Date.parse(created_at)),
      filterComponent: (props) => <MTDateRangePicker
        initialStartDate={undefined}
        initialEndDate={undefined}
        onChangeStartDate={()=>{}}
        onChangeEndDate={()=>{}}
        onFilterChanged={props.onFilterChanged}
        columnDef={props.columnDef}
      />
    },
    {
      title: <FormattedMessage id={"status"}/>,
      field: "status",
      type: "string",
      headerStyle: {minWidth: 180},
      lookup: ["waiting_for_agent", "waiting_for_resale", "waiting_for_enterprise", "approved", "rejected", "archived", "undefined"]
        .reduce(
          (acc, status) => ({...acc, [status]: intl.formatMessage({id: status})}),
          {}
        ),
      pagination: {
        spech: {
          column_name: "status",
          filter_operator: "in",
          value_parser: (value) => value === "undefined" ? null : value,
        }
      }
    },
    {
      title: <FormattedMessage id="total"/>,
      field: "val_discounted_total",
      render: ({val_total}) =>
        val_total?
          formatCurrency( val_total)
          : "-",
      filtering: false,
    },
];

export const OPTIONS = {
  filtering: true,
  toolbar: false,
  sorting: true,
  pageSizeOptions: [5, 10, 20],
  headerStyle: {
    fontWeight: "bold",
    fontSize: 15,
    whitespace: "nowrap",
  },
  cellStyle: {
    textTransform: "uppercase",
  }
};

export const LOCALIZATION = {
  header: {
    actions: "",
  },
  body: {
    emptyDataSourceMessage: "Nessun preventivo disponibile",
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count} righe",
    labelRowsSelect: "righe",
  },
};
